<template>
  <div
    class="ttf ttf2 ttf--2 lp-section width-expand min-height-page"
    :id="`Ttf2-${pageUid}`">
    <div class="top-wrapper">
      <div
        class="logo-container-mobile padding-l flex-middle flex-center l:hidden"
        :class="`bg-${legalBackgroundColor}`">
        <anchor
          v-if="logo.show"
          :href="sanitizeLink(logo.href)"
          :target="logo.target"
          class="logo inline-block logo logo--1 flex-middle object-contain"
          @click.prevent="
            trackEvent('ttf2|logo1|click').then(logo.navigationHandler)
          ">
          <img
            :src="logo.finalSrc"
            :alt="logo.alt"
            style="
              max-width: 100%;
              max-height: calc(var(--base-margin) * 40);
            " />
        </anchor>
      </div>
      <div
        class="content-side flex-between flex-column gap-l"
        :class="`bg-${backgroundColor}`">
        <div class="content-block">

          <h1
            class="main-title bold margin-l-bottom l:visible"
            v-html="sanitizeHtml(nl2Br(title))"
            :class="`c-${mainTitleColor}`">
          </h1>
          <h1
            class="main-title bold margin-l-bottom l:hidden margin-xl-bottom"
            v-html="sanitizeHtml(nl2Br(mobileTitle))"
            :class="`c-${mainTitleColor}`">
          </h1>
          <div
            v-if="yieldHighlights && yieldHighlights.length > 0"
            class="flex-middle flex-start margin-m-vertical l:margin-xl-block flex-column l:flex-row gap-m l:gap-2xl flex-stretch">
            <div class="yield-block flex-stretch gap-2xl" v-for="(item, index) of yieldHighlights">
              <div>
                <div>
                  <span
                    class="yield-block__percent"
                    :class="`c-${highlightedYieldsPercentColor}`"
                    >{{ item.percent }}</span
                  >
                </div>
                <div>
                  <span
                    class="yield-block__text"
                    :class="`c-${highlightedYieldsTextColor}`"
                    >{{ item.text }}</span
                  >
                </div>
              </div>
              <div class="yield-block__separator l:visible"
                   :class="`bg-${highlightedYieldsPercentColor}`"
                   v-if="index % 2 == 0"></div>
            </div>
          </div>

          <div>
            <h3
              class="sub-title bold l:visible margin-remove"
              :class="`c-${subTitleColor}`"
              v-html="forceLtrInString(sanitizeHtml(nl2Br(subTitle)))"></h3>
            <h3
              class="sub-title bold l:hidden margin-remove"
              :class="`c-${subTitleColor}`"
              v-html="forceLtrInString(sanitizeHtml(nl2Br(mobileSubTitle)))"></h3>

            <p
              class="content-text relative l:visible relative"
              :class="`c-${textColor}`">
              <span
                class="base-content"
                :class="`c-${textColor}`"
                v-html="sanitizeHtml(nl2Br(text))"></span>
              <span v-if="showTextSuperset" class="superscript">1</span>
            </p>

            <p
              class="content-text l:hidden relative margin-remove"
              :class="`c-${textColor}`">
              <span
                class="base-content"
                :class="`c-${textColor}`"
                v-html="sanitizeHtml(text)"></span>
              <span v-if="showTextSuperset" class="superscript">1</span>
            </p>

            <p
              class="content-text content--text-ttf margin-remove"
              :class="`c-${ttfTextColor}`">
              {{ ttfText }}
            </p>

            <div
              class="flex-stretch flex-center flex-column l:flex-row margin-m-top l:margin-xl-top gap-2xl">
              <div class="feature border-radius-m  relative "
                   :class="`c-${featureBorderColor}`" v-for="feature in features">
                <img class="feature__icon"
                     :class="`bg-${backgroundColor} c-${backgroundColor}`"
                     v-if="feature.icon"
                     :src="feature.icon" alt="lp.sections.ttf2.fields.features.iconAlt">
                <div>
                  <span class="feature__text-wrapper" :class="`c-${featureTextColor}`">
                    <span class="feature__text">{{feature.text1}}</span>
                    <span class="feature__text feature__text--large ">{{feature.text2}}</span>
                    <span class="feature__text" >{{feature.text3}}</span>
                    <span class="feature__text--superset" v-if="isNonEmptyString(feature.supersetText)">{{feature.supersetText}}</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>


        <div class="links-content" style="">
          <hr
            class="links-separator block"
            style="background-color: var(--c-bg); opacity: 0.7; width: 100%" />
          <h3
            class="links-title text-normal text-2xl"
            :class="`c-${linksTitleColor}`">
            {{ linksTitle }}
          </h3>
          <div
            class="link-wrapper flex flex-wrap flex-center l:flex-start flex-stretch gap-l padding-l-bottom l:padding-remove-bottom">
            <a
              :href="sanitizeLink(link.href)"
              :target="logo.target"
              @click.prevent="
                trackEvent('ttf2|heroLink|click', { value: link.text }).then(
                  link.navigationHandler
                )
              "
              class="flex relative plain-link link-item flex-middle flex-center relative text-center"
              :style="`border-color: var(--c-${linkBorderColor});`"
              v-for="{ link } in links">
              <div
                class="block plain-link link-bg absolute-cover z-level-1"
                :class="`bg-${linkBorderColor}`"
                style="opacity: 0.1"></div>
              <span
                class="block width-expand relative z-level-2 text-center text-decoration-none"
                :class="`c-${linkColor}`">
                <span>{{ link?.text }}</span>
              </span>
              <span class="forward-icon-wrapper">
                <icon :theme="linkColor" icon="chevrons-inline-end"></icon>
              </span>
            </a>
          </div>
        </div>
      </div>
      <div class="sides-separator" :class="`bg-${separatorColor}`"></div>
      <div class="media-side" style="">
        <img
          class="width-expand size-parent"
          :src="mainImage.finalSrc"
          :alt="mainImage.alt"
          style="object-fit: cover" />
      </div>
    </div>
    <div
      class="legal-area padding-l l:padding-xl"
      :class="`bg-${legalBackgroundColor}`">
      <div class="legal-top gap-2xl flex-between flex-stretch">
        <div class="legal-text flex flex-middle" :class="`c-${legalColor}`">
          <content-blocks :content="legalText.blocks"></content-blocks>
        </div>
        <div
          class="separator l:visible margin-s-vertical"
          :class="`bg-${legalColor}`"
          style="min-width: 1px !important; opacity: 0.5"></div>
        <div class="logo flex flex-middle flex-center l:flex-end l:visible">
          <anchor
            v-if="logo.show"
            :href="sanitizeLink(logo.href)"
            :target="logo.target"
            class="logo inline-block logo logo--1 flex-middle object-contain"
            @click.prevent="
              trackEvent('ttf2|logo1|click').then(logo.navigationHandler)
            ">
            <img
              :src="logo.finalSrc"
              :alt="logo.alt"
              style="
                max-width: calc(var(--base-margin) * 320px);
                max-height: calc(var(--base-margin) * 80);
              " />
          </anchor>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import useUiElements from "@/client/extensions/composition/useUiElements";
import lpSectionMixin from "@/client/applications/meitavlp/extensions/mixins/lpSection";
import { inject } from "vue";

export default {
  mixins: [lpSectionMixin],

  setup(props) {
    let pageUid = inject("pageUid");

    let schema = {
      title: "",
      mobileTitle: "",
      subTitle: "",
      mobileSubTitle: "",
      text: "",
      ttfText: "",
      showTextSuperset: false,
      linksTitle: "",
      links: [],
      mainImage: {},
      yieldHighlights: [],
      features: [],
      legalNotice: "",
      legalText: "",
      logo: {},
      backgroundColor: "alt",
      separatorColor: "bg",
      legalBackgroundColor: "bg",
      legalColor: "inverse",
      mainTitleColor: "comp-lead",
      highlightedYieldsPercentColor: "bg",
      highlightedYieldsTextColor: "bg",
      subTitleColor: "bg",
      textColor: "bg",
      ttfTextColor: "comp-lead",
      featureTextColor: "bg",
      featureBorderColor: "comp-lead",
      linksTitleColor: "bg",
      linkBorderColor: "bg",
      linkColor: "bg",

    };
    let { wrapUiElements } = useUiElements();
    let overloadedProps = useUiElements({ input: props.sectionData }).props;

    overloadedProps.links.map((linkObject) => wrapUiElements(linkObject));

    return {
      ...schema,
      ...overloadedProps,
      allData: overloadedProps,
      pageUid,
      trackEvent: inject("trackEvent"),
      track: inject("track"),
      triggerSectionsEvent: inject("triggerSectionsEvent"),
    };
  },
  data: function () {
    return {};
  },
  computed: {},
  methods: {},
};
</script>

<style scoped lang="scss">
.ttf2 {
  --c-comp-lead-1: #e75971;
  --c-comp-lead-2: #e02d4b;
  --c-comp-lead: #be1c37;
  --c-comp-lead-4: #92152a;
  --c-comp-lead-5: #650f1d;

  .top-wrapper {
    display: flex;
    @media (max-width:1023px) {
      flex-direction: column;
    }
  }
  .logo-container-mobile {
    order: 1;
  }

  .content-side {
    flex: 0 0 calc(50% - 1px);
    padding: var(--margin-xl) var(--margin-2xl);

    @media (max-width: 1024px) {
      flex: auto;
      width: 100%;
      order: 4;
      padding: var(--margin-l)  var(--margin-xl);

    }

    @media (max-width: 359px) {
      flex: auto;
      width: 100%;
      order: 4;
      padding: var(--margin-l)  var(--margin-xl);
    }
  }

  .media-side {
    flex: 0 0 calc(50% - 1px);

    @media (max-width: 1024px) {
      flex: 0 0 calc(var(--base-margin) * 300); // set the height for the image
      max-height: calc(var(--base-margin) * 300);
      width: 100%;
      order: 2;
    }
  }

  .sides-separator {
    flex: 0 0 2px;
    min-width: 2px;

    @media (max-width: 1024px) {
      width: 100%;
      min-height: 2px;
      order: 3;
    }
  }

  .main-title {
    font-size: calc(var(--font-jumbo) * 1.5);
    line-height: 0.7;
    letter-spacing: -1.8px;

    span, br {
      line-height: 0.7;
      letter-spacing: -1.8px;
    }


    @media (max-width: 1024px) {
      font-size: calc(var(--font-jumbo) * 1.2);
      line-height: 0.8;
      letter-spacing: -0.8px;


      span, br {
        line-height: 0.8;
        letter-spacing: -0.8px;
      }
    }
  }

  .sub-title {
    font-size: calc(var(--font-max) * 0.75);
    line-height: 0.87;
    letter-spacing: -1.1px;

    @media (max-width: 1024px) {
      font-size: calc(var(--font-max) * 0.55);
      line-height: 0.87;
      letter-spacing: -0.6px;
      margin-bottom: var(--margin-xl);
    }

    // THIS STYLE DOES NOT WORK because it is using v-html.
    // below is the global style effecting it. this is here for reference
    // so future persons do not lose their minds
    br,
    span {
      line-height: 1;
      letter-spacing: -1.3px;

      @media (max-width: 1024px) {
        line-height: 1;
        letter-spacing: -0.8px;
      }
    }
  }

  .yield-block {
    @media (max-width:1024px) {
      margin-bottom: var(--margin-l);
    }

    &__percent {
      font-size: calc(var(--font-jumbo) * 1.3);
      font-weight: bold;
      line-height: 0.8;

      @media (max-width:1024px) {
        font-size: calc(var(--font-jumbo) * 1);
        font-weight: bold;
        line-height: 0.8;
      }
    }

    &__text {
      font-size: calc(var(--font-2xl) * 0.6);

      @media (max-width:1024px) {
        font-size: calc(var(--font-l) * 1.15);
        font-weight: normal;
        line-height: 0.8;
      }
    }

    &__separator {
      min-width: 2px;
      background-color: white!important;
      min-height: 100%;
      opacity: 40%;
    }
  }

  .content-text,
  .content-text--ttf {
    display: inline-block;
    font-size: calc(var(--font-3xl) * 1);
    line-height: 0.9;

    .superscript {
      display: inline-block;
      position: relative;
      font-size: 0.6em;
      top: -0.8em;
      inset-inline-start: 0.2em;
    }

    @media (max-width: 1024px) {
      font-size: calc(var(--font-2xl) * 1);
      line-height: 1;
      letter-spacing: -0 0.5px;
    }
  }

  .feature {
    border: 3px solid;
    line-height: 0.85;
    font-weight: 600;
    padding-top: var(--margin-m);
    padding-bottom: var(--margin-l);
    padding-inline-start: calc(var(--margin-l) + 24 * var(--base-margin));
    padding-inline-end: var(--margin-m);

    @media (max-width: 1024px) {}

    &__text-wrapper {

    }

    &__text {
      font-size: calc(var(--font-2xl) * 1.0);
      line-height: 0.85;


    }

    &__text--large {
      position: relative;
      top: -2px;
      font-size:  calc(var(--font-3xl) * 1.0);
      line-height: 0.85;
    }

    &__text--superset {
      position: relative;
      top: -6px;
      font-size:  calc(var(--font-3xl) * 0.5);
      line-height: 0.85;
    }

    &__icon {
      position: absolute;

      width: calc(var(--base-margin) * 48);
      height: auto;
      inset-inline-start: calc((var(--base-margin) * 48) / 2 * -1);

      top: 50%;
      transform: translateY(-50%);
      z-index: 2;
      padding-block: calc(var(--base-margin) * 6);
    }
  }

  .links-separator{
    margin-block: var(--margin-2xl);


    @media (max-width: 1024px) {
      margin-block: var(--margin-2xl);
    }
  }
  .links-title {
    letter-spacing: -0.6px;
    line-height: 0.9;

    @media (max-width: 1024px) {
      line-height: 1;
      letter-spacing: -0.56px;
    }
  }

  .link-wrapper {
  }

  .link-item {
    padding-block: calc(var(--margin-m) * 1.35);
    padding-inline: var(--margin-m);
    border-radius: var(--border-radius);
    border: 1px solid var(--c-bg);
    font-size: calc(var(--font-l) * 1.1);
    font-weight: bold;
    line-height: 0.9;
    min-width: calc(var(--base-margin) * 150);
    max-width: calc(var(--base-margin) * 150);

    @media (max-width: 767px) {
      flex: calc(50% - var(--margin-l));
      min-height: calc(var(--base-margin) * 106);
    }

    @media (max-width: 359px) {
      // not 140 to avoid rendering edge cases which will make it 1 per row
      min-width: calc(var(--base-margin) * 139);
      max-width: calc(var(--base-margin) * 139);
    }

    .forward-icon-wrapper {
      position: absolute;
      bottom: 0;
      inset-inline-end: 0;
      z-index: var(-z-2);
    }
  }

  .legal-top {
    .content {
      text-align: center;
      @media (max-width: 1024px) {
        text-align: start;
      }
    }
  }
}
</style>

<style lang="scss">
.ttf2 .content-side .content-block {
  .sub-title br,
  .sub-title span {
    line-height: 0.87;
    letter-spacing: -0.7px;

    @media (max-width: 1024px) {
      line-height: 0.87;
      letter-spacing: -0.6px;
    }
  }

  .content-text,
  .content-text--ttf {
    span:not(.base-content) {
      unicode-bidi: bidi-override;
    }
  }
}
</style>
